'use client'

import { useState } from 'react'
import { Button } from '@vinted/web-ui'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useRefUrl } from '@marketplace-web/ref-url/ref-url-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { navigateToPage } from '@marketplace-web/shared/browser-util'
import {
  clickEvent,
  acceptTermsAndConditions,
} from '@marketplace-web/terms-and-conditions/terms-and-conditions-data'

import { ROOT_URL } from '../../constants/routes'

type Props = {
  showAcceptErrorModal: (value: boolean) => void
}

const TermsActions = ({ showAcceptErrorModal }: Props) => {
  const breakpoints = useBreakpoint()
  const refUrl = useRefUrl(ROOT_URL)
  const { track } = useTracking()
  const { user } = useSession()
  const [isAcceptButtonDisabled, setIsAcceptButtonDisabled] = useState(false)

  const translate = useTranslate('terms_and_conditions.actions')
  const isRestrictedByTerms = user?.restricted_by_terms

  const buttonSize = breakpoints.tabletsUp ? undefined : Button.Size.Medium
  const screen = isRestrictedByTerms ? 'force_accept_terms_and_conditions' : 'terms_view'

  const trackClick = (target: Parameters<typeof clickEvent>[0]['target']) => {
    track(clickEvent({ target, screen, env: 'js' }))
  }

  const handleAcceptOnTerms = async () => {
    trackClick('accept_terms')
    if (!user?.id) return

    setIsAcceptButtonDisabled(true)

    const response = await acceptTermsAndConditions(user.id)
    if ('errors' in response) {
      setIsAcceptButtonDisabled(false)
      showAcceptErrorModal(true)

      return
    }

    navigateToPage(refUrl)
  }

  return (
    <Button
      text={translate('agree')}
      disabled={isAcceptButtonDisabled}
      styling={Button.Styling.Filled}
      size={buttonSize}
      onClick={handleAcceptOnTerms}
      testId="terms-button-agree"
    />
  )
}

export default TermsActions
